import styles from "./RequestAccount.module.css";

import React, { useState, useEffect } from "react";

import { NavLink } from "react-router-dom";

import { Grid, TextField, MenuItem, Button } from "@material-ui/core";
import {
  hasLength,
  areAllPropertiesEmpty,
  isAuthenticationCode,
  isEmpty,
  isNotEmpty,
  validatePhone,
} from "../../../utils/Validator.js";
import { getValue } from "../../../utils/Utils.js";

import { ProviderForm } from "./ProviderForm";
import { ProviderList } from "./ProviderList";

import GlobalMessage from "../../GlobalMessage";
import { LabeledControlGroup } from "../../LabeledControlGroup";
import LoadingMsgBar from "../../LoadingMsgBar";

import RegistrationService from "../../../Services/RegistrationService";

// Extracted constants
const AFFILIATION_OPTIONS = [
  { value: "SELECT_ONE", label: "Select One" },
  {
    value: "Provider Staff or Facility Employee",
    label: "Provider Staff or Facility Employee",
  },
  {
    value: "Third Party Billing Company",
    label: "Third Party Billing Company",
  },
  { value: "Other", label: "Other" },
];

const INITIAL_FORM_STATE = {
  token: "",
  authenticationCode: "",
  affiliation: "SELECT_ONE",
  otherAffiliation: "",
  firstName: "",
  lastName: "",
  middleInitial: "",
  phoneNumber: "",
  jobTitle: "",
  companyName: "",
  companyUrl: "",
  providerList: [],
};

const INITIAL_ERROR_STATE = {
  authenticationCode: "",
  affiliation: "",
  otherAffiliation: "",
  firstName: "",
  lastName: "",
  middleInitial: "",
  phoneNumber: "",
  jobTitle: "",
  companyName: "",
  companyUrl: "",
  providerList: "",
};

function RequestAccount({ history, location }) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [errors, setErrors] = useState(INITIAL_ERROR_STATE);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [processingMessage, setProcessingMessage] = useState(null);
  const [providers, setProviders] = useState([]);
  const [viewState, setViewState] = useState("LOADING"); // Possible values: "LOADING", "LINK_EXPIRED", "FORM_ACTIVE"
  const [disableSubmit, setDisableSubmit] = useState(false);

  const updateForm = (newData) => {
    setFormData((prevData) => ({
      ...prevData,
      ...newData,
    }));
  };

  const isLinkExpired = (expirationTime) => {
    return expirationTime < Date.now();
  };

  useEffect(() => {
    // Extract query parameters from location
    let queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");
    const expirationTime = getValue(queryParams.get("expire"));
    setViewState("FORM_ACTIVE");

    // Determine which view to show
    if (!token) {
      setViewState("INVALID_LINK");
    } else if (isLinkExpired(expirationTime)) {
      setViewState("LINK_EXPIRED");
    } else {
      updateForm({ token: token });
      setViewState("FORM_ACTIVE");
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    updateForm({ [name]: value });
  };

  // return true if form is valid
  const validateForm = () => {
    const newErrors = { ...INITIAL_ERROR_STATE };

    if (isEmpty(formData.authenticationCode)) {
      newErrors.authenticationCode = "Error: Enter Authentication Code";
    } else if (!isAuthenticationCode(formData.authenticationCode)) {
      newErrors.authenticationCode = "Invalid Authentication Code";
    }

    if (!hasLength(formData.firstName, 2)) {
      newErrors.firstName = "First Name must have at least 2 letters.";
    }

    if (formData.affiliation === "SELECT_ONE") {
      newErrors.affiliation = "Affiliation is required.";
    }

    if (
      formData.affiliation === "Other" &&
      !hasLength(formData.otherAffiliation, 2)
    ) {
      newErrors.otherAffiliation =
        "Other Affiliation must have at least 2 letters.";
    }

    if (!hasLength(formData.firstName, 2)) {
      newErrors.firstName = "First Name must have at least 2 letters.";
    }

    if (!hasLength(formData.lastName, 2)) {
      newErrors.lastName = "Last Name must have at least 2 letters.";
    }

    if (!validatePhone(formData.phoneNumber)) {
      newErrors.phoneNumber = "A valid Phone Number is required.";
    }

    if (!hasLength(formData.jobTitle, 2)) {
      newErrors.jobTitle = "Job title must have at least 2 letters.";
    }

    if (!hasLength(formData.companyName, 2)) {
      newErrors.companyName = "Company name must have at least 2 letters.";
    }

    if (isEmpty(formData.providerList)) {
      newErrors.providerList = "Provider information is required.";
    }

    setErrors(newErrors);

    return areAllPropertiesEmpty(newErrors);
  };

  const handleAddProvider = (provider) => {
    const updatedProviders = [...providers, provider];
    setProviders(updatedProviders);
    setFormData({
      ...formData,
      providerList: updatedProviders,
    });
  };

  const handleUpdateProvider = (updatedProvider) => {
    if (editingIndex === null) return;

    const updatedProviders = [...providers];
    updatedProviders[editingIndex] = updatedProvider;

    setProviders(updatedProviders);
    setFormData({
      ...formData,
      providerList: updatedProviders,
    });
    setEditingIndex(null);
  };

  const handleEditProvider = (index) => {
    setEditingIndex(index);
  };

  const handleDeleteProvider = (index) => {
    const updatedProviders = [...providers];
    updatedProviders.splice(index, 1);

    setProviders(updatedProviders);
    setFormData({
      ...formData,
      providerList: updatedProviders,
    });

    if (editingIndex === index) {
      setEditingIndex(null);
    }
  };

  const handleSubmit = async () => {
    setErrorMessage(null);

    if (validateForm()) {
      try {
        setDisableSubmit(true);
        setProcessingMessage("Please wait while we process your request.");

        await RegistrationService.submitRequestAccountForm(formData);
        setViewState("COMPLETE");
      } catch (error) {
        const localizedMessage = error?.response?.data?.localizedMessage;

        if (localizedMessage?.startsWith("INVALID_TOKEN")) {
          setErrorMessage("This authentication code has already been used or is invalid.");
        } else {
          setErrorMessage(`There was an error processing your request. Please try again. If it persists, please email Coventry Provider Portal Support at <a href="mailto:coventryprovidertechsupport@cvty.us.com?subject=Coventryprovider.com%20Account%20Request%20-%20Submission%20Error">coventryprovidertechsupport@cvty.us.com</a>.`);
        }

        console.error(error);
      } finally {
        setProcessingMessage(null);
        setDisableSubmit(false);
      }
    }
  };

  const handleCancel = () => {
    history.push("/wcpp/login");
  };

  const renderLinkExpired = () => {
    return (
      <div className="registration">
        <h1 className="pageTitle">Coventry Provider Portal Account Request</h1>

        <p>
          Your activation link has expired, please{" "}
          <NavLink autoFocus className="Blue-font" to="/wcpp/emailRegistration">
            restart registration
          </NavLink>
          .
        </p>
      </div>
    );
  };

  const renderThankYou = () => {
    return (
      <div>
        <h1 className="pageTitle">Coventry Provider Portal Account Request</h1>

        <p>
          Thank you for your request. Your application has been successfully
          received and is currently under review by our support team.
        </p>
      </div>
    );
  };

  const renderForm = () => {
    return (
      <div className={styles.requestAccount}>
        <h1 className={styles.pageTitle}>
          Coventry Provider Portal Account Request
        </h1>

        <LoadingMsgBar loadMsg={processingMessage} />

        <GlobalMessage message={errorMessage} type="error" />

        <div className="App-note-Field App-error-font App-error-color">
          Note: Fields marked with an asterisk(*) are required.
        </div>

        <Grid container spacing={3}>
          <Grid item xs={4}>
            <TextField
              id="authenticationCode"
              name="authenticationCode"
              label="Authentication Code"
              error={isNotEmpty(errors.authenticationCode)}
              helperText={errors.authenticationCode}
              value={formData.authenticationCode}
              onChange={handleChange}
              inputProps={{ maxLength: 23 }}
              fullWidth
              required
              autoFocus
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <TextField
              name="affiliation"
              id="affiliation"
              label="Affiliation"
              error={isNotEmpty(errors.affiliation)}
              helperText={errors.affiliation}
              select
              value={formData.affiliation}
              onChange={handleChange}
              fullWidth
              required
            >
              {AFFILIATION_OPTIONS.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {formData.affiliation === "Other" && (
            <Grid item xs={4}>
              <TextField
                id="otherAffiliation"
                name="otherAffiliation"
                label="Affiliation (If Other)"
                error={isNotEmpty(errors.otherAffiliation)}
                helperText={errors.otherAffiliation}
                value={
                  formData.affiliation === "Other"
                    ? formData.otherAffiliation
                    : ""
                }
                onChange={handleChange}
                inputProps={{ maxLength: 128 }}
                fullWidth
                required
              />
            </Grid>
          )}
        </Grid>

        {/* Personal Information */}
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <TextField
              id="firstName"
              name="firstName"
              label="First Name"
              error={isNotEmpty(errors.firstName)}
              helperText={errors.firstName}
              value={formData.firstName}
              onChange={handleChange}
              inputProps={{ maxLength: 40 }}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={2}>
            <TextField
              id="middleInitial"
              name="middleInitial"
              label="Middle Initial"
              error={isNotEmpty(errors.middleInitial)}
              helperText={errors.middleInitial}
              value={formData.middleInitial}
              onChange={handleChange}
              inputProps={{ maxLength: 1 }}
              fullWidth
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              id="lastName"
              name="lastName"
              label="Last Name"
              error={isNotEmpty(errors.lastName)}
              helperText={errors.lastName}
              value={formData.lastName}
              onChange={handleChange}
              inputProps={{ maxLength: 40 }}
              fullWidth
              required
            />
          </Grid>
        </Grid>

        {/* Contact Information */}
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <TextField
              id="phoneNumber"
              name="phoneNumber"
              label="Phone Number"
              error={isNotEmpty(errors.phoneNumber)}
              helperText={errors.phoneNumber}
              value={formData.phoneNumber}
              onChange={handleChange}
              inputProps={{ maxLength: 14 }}
              fullWidth
              required
            />
          </Grid>
        </Grid>

        {/* Employment Information */}
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <TextField
              id="jobTitle"
              name="jobTitle"
              label="Job Title"
              error={isNotEmpty(errors.jobTitle)}
              helperText={errors.jobTitle}
              value={formData.jobTitle}
              onChange={handleChange}
              inputProps={{ maxLength: 128 }}
              fullWidth
              required
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <TextField
              id="companyName"
              name="companyName"
              label="Company Name"
              error={isNotEmpty(errors.companyName)}
              helperText={errors.companyName}
              value={formData.companyName}
              onChange={handleChange}
              inputProps={{ maxLength: 128 }}
              fullWidth
              required
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <TextField
              id="companyUrl"
              name="companyUrl"
              label="Company URL"
              error={isNotEmpty(errors.companyUrl)}
              helperText={errors.companyUrl}
              value={formData.companyUrl}
              onChange={handleChange}
              inputProps={{ maxLength: 128 }}
              fullWidth
            />
          </Grid>
        </Grid>

        {/* Provider information section */}
        <Grid item xs={6}>
          <LabeledControlGroup
            label="Provider Information (Maximum 1 provider)"
            required
            validationError={errors.providerList}
          >
            <p className={styles.ftinWarning}>
              FTIN information will be reviewed for appropriate network
              association.
            </p>

            {editingIndex !== null ? (
              <ProviderForm
                onUpdateProvider={handleUpdateProvider}
                initialProvider={providers[editingIndex]}
                onCancel={() => setEditingIndex(null)}
                providers={providers}
              />
            ) : (
              <ProviderForm
                onAddProvider={handleAddProvider}
                providers={providers}
              />
            )}

            <ProviderList
              className={styles.providerList}
              providers={providers}
              onEdit={handleEditProvider}
              onDelete={handleDeleteProvider}
            />
          </LabeledControlGroup>
        </Grid>

        <div className="buttonBar">
          <Button onClick={handleSubmit} type="button" disabled={disableSubmit}>
            Done
          </Button>
          <Button onClick={handleCancel} type="button" variant="outlined">
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  const renderInvalidLink = () => {
    return (
      <div className="registration">
        <h1 className="pageTitle">Coventry Provider Portal Account Request</h1>

        <p>
          Invalid activation link. Please{" "}
          <NavLink autoFocus className="Blue-font" to="/wcpp/emailRegistration">
            restart registration
          </NavLink>
          .
        </p>
      </div>
    );
  };

  const renderContent = () => {
    switch (viewState) {
      case "LOADING":
        return <div>Loading...</div>;
      case "LINK_EXPIRED":
        return renderLinkExpired();
      case "INVALID_LINK":
        return renderInvalidLink();
      case "FORM_ACTIVE":
        return renderForm();
      case "COMPLETE":
        return renderThankYou();
      default:
        return <div>Something went wrong. Please try again.</div>;
    }
  };

  return renderContent();
}

export { RequestAccount };
